import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/utils';

type Status = 'pending' | 'published' | 'rejected';

interface Row {
  review: {
    id: string;
    rating: number;
    title: string | null;
    body: string | null;
    locale: string;
    createdAt: string;
  };
  productName: string | null;
  orderNumber: string;
}

/** Модерация отзывов: публикуются только проверенные отзывы реальных покупателей. */
export function ReviewsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('pending');
  const list = useQuery({
    queryKey: ['reviews', status],
    queryFn: () => api<{ items: Row[] }>(`/reviews?status=${status}`),
  });
  const act = async (id: string, action: 'publish' | 'reject') => {
    await api(`/reviews/${id}/${action}`, { method: 'POST' });
    await queryClient.invalidateQueries({ queryKey: ['reviews'] });
  };

  return (
    <>
      <PageHeader title={t('rev.title')} description={t('rev.hint')} />
      <div className="mb-4 flex gap-2" role="tablist">
        {(['pending', 'published', 'rejected'] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            role="tab"
            aria-selected={s === status}
            variant={s === status ? 'default' : 'outline'}
            onClick={() => {
              setStatus(s);
            }}
          >
            {t(`rev.status.${s}`)}
          </Button>
        ))}
      </div>
      {list.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('rev.empty')}</p>
      ) : null}
      <div className="grid gap-3">
        {(list.data?.items ?? []).map(({ review, productName, orderNumber }) => (
          <Card key={review.id} data-testid="review-card">
            <CardHeader
              title={`${'★'.repeat(review.rating)}${'☆'.repeat(5 - review.rating)} · ${productName ?? ''}`}
              description={`${orderNumber} · ${review.locale} · ${formatDateTime(review.createdAt, i18n.language)}`}
            />
            <CardContent className="grid gap-2 text-sm">
              {review.title ? <p className="font-medium">{review.title}</p> : null}
              {review.body ? <p className="whitespace-pre-line">{review.body}</p> : null}
              {status === 'pending' ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => void act(review.id, 'publish')}>
                    {t('rev.publish')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void act(review.id, 'reject')}>
                    {t('rev.reject')}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
